import { ref, watch, type Ref } from 'vue';
import { Vector3 } from 'three';
import {
    setCameraPlacement,
    type CameraPlacementId,
    type CameraPlacementModel,
    type CameraPlacementScene,
} from '@/views/3d-planner/scene/cameraPlacement';
import { vector3sApproxEqual } from '@/domain/geometry/approxEquals';
import { stopAll, type StopHandle } from '@/util/stopHandle';

export type ActivePresetTracker = {
    readonly activePreset: Readonly<Ref<CameraPlacementId | null>>;
    setPreset(id: CameraPlacementId): void;
    stop: StopHandle;
};

export function useActivePresetTracker(
    model: CameraPlacementModel,
    scene: CameraPlacementScene,
): ActivePresetTracker {
    const activePreset = ref<CameraPlacementId | null>(null);

    // We only care about *orientation* — the direction the camera looks and
    // its up-vector. Pan moves position+target together (view-direction
    // unchanged), zoom moves position along the view-direction (also
    // unchanged), so neither should invalidate the preset. Orbit changes
    // the view-direction, which does.
    const canonical = {
        viewDirection: new Vector3(),
        up: new Vector3(),
    };

    function captureOrientation(): { viewDirection: Vector3; up: Vector3 } {
        const c = scene.rendering.camera;
        return {
            viewDirection: c.target.clone().sub(c.position).normalize(),
            up: c.up.clone().normalize(),
        };
    }

    function setPreset(id: CameraPlacementId): void {
        setCameraPlacement(model, scene, id);
        const orientation = captureOrientation();
        canonical.viewDirection.copy(orientation.viewDirection);
        canonical.up.copy(orientation.up);
        activePreset.value = id;
    }

    const stopCameraWatch = watch(
        () => [
            scene.rendering.camera.position,
            scene.rendering.camera.target,
            scene.rendering.camera.up,
        ],
        () => {
            if (activePreset.value === null) return;
            const current = captureOrientation();
            if (
                !vector3sApproxEqual(current.viewDirection, canonical.viewDirection) ||
                !vector3sApproxEqual(current.up, canonical.up)
            ) {
                activePreset.value = null;
            }
        },
        { deep: true },
    );

    // Reorient to the "default" camera placement whenever the planner mode
    // changes — and mark the Default preset as active so the snap-to-view bar
    // highlights it. The 'default' placement dispatches to the mode-specific
    // view (stem → stem, cup → cup, otherwise scene-centre fallback).
    // 'disabled' mode is skipped (no placement).
    const stopModeWatch = watch(
        () => scene.plannerMode,
        (mode) => {
            if (mode === 'disabled') return;
            setPreset('default');
        },
        { immediate: true },
    );

    return { activePreset, setPreset, stop: stopAll(stopCameraWatch, stopModeWatch) };
}
